import React, { useState } from "react";
import { FormInput } from "../../../components/form";
import { Search } from "../../../components/search/Search.tsx";
import { SearchContextProvider } from "../../../contexts/search.tsx";
import { useUsersContext } from "../../../contexts/users.tsx";
import { Journal } from "../../../models/journals/journal";
import formatNumber from "../../../models/helpers/formatNumber.ts";
import CustomIcon from "../../../components/CustomIcon.tsx";
import IsNullOrEmpty from "../../../models/helpers/isNullOrEmpty.ts";

export const JournalGroup = ({
    journalGroup,
    handleInputChange,
    handleAddItem,
    handleDeleteItem,
    mb = 3,
    displayName = false
}: {
    journalGroup: Journal[];
    handleInputChange?: any;
    handleAddItem?: any;
    handleDeleteItem?: any;
    mb?: number;
    displayName?: boolean;
}) => {
    const [showAddItem, setShowAddItem] = useState<boolean>(false);
    const {
        measurementUnits
    } = useUsersContext()

    const getMeasurementUnit = (journal: Journal) => {
        return measurementUnits.find(x => x.measurementUnitID == (journal.aliment?.measurementUnitID ?? 1))?.name
    }

    const getCalories = (journal: Journal) => {
        return formatNumber.ToFixed(journal.amount * journal.nutritionalValues?.calories / 100);
    }

    const getGroupCalories = (journalGroup: Journal[]) => {
        return formatNumber.ToFixed(
            journalGroup.reduce((total, journal) => {
                return total + (journal.amount * (journal.nutritionalValues?.calories || 0)) / 100;
            }, 0)
        );
    };

    const getProtein = (journal: Journal) => {
        return formatNumber.ToFixed(journal.amount * journal.nutritionalValues?.protein / 100);
    }

    const getGroupProtein = (journalGroup: Journal[]) => {
        return formatNumber.ToFixed(
            journalGroup.reduce((total, journal) => {
                return total + (journal.amount * (journal.nutritionalValues?.protein || 0)) / 100;
            }, 0)
        );
    };

    const getCarbohydrates = (journal: Journal) => {
        return formatNumber.ToFixed(journal.amount * journal.nutritionalValues?.carbohydrates / 100);
    }

    const getGroupCarbohydrates = (journalGroup: Journal[]) => {
        return formatNumber.ToFixed(
            journalGroup.reduce((total, journal) => {
                return total + (journal.amount * (journal.nutritionalValues?.carbohydrates || 0)) / 100;
            }, 0)
        );
    };

    const getTotalFat = (journal: Journal) => {
        return formatNumber.ToFixed(journal.amount * journal.nutritionalValues?.totalFat / 100);
    }

    const getGroupTotalFat = (journalGroup: Journal[]) => {
        return formatNumber.ToFixed(
            journalGroup.reduce((total, journal) => {
                return total + (journal.amount * (journal.nutritionalValues?.totalFat || 0)) / 100;
            }, 0)
        );
    };

    return (
        <>{journalGroup.length > 0 &&
            <ul className={`list-group ${mb && mb > 0 && `mb-${mb}`}`}>
                {displayName && <li className="list-group-item bg-body-secondary">
                    <h5 className="my-auto">{journalGroup[0].name}</h5>
                    <div className="d-flex flex-row justify-content-between mw-100 mt-1">
                        <div className="w-20 my-auto text-center">{getGroupCalories(journalGroup)}kCal</div>
                        <div className="w-20 my-auto text-center text-success">
                            <CustomIcon name='protein' />
                            {getGroupProtein(journalGroup)}g
                        </div>
                        <div className="w-20 my-auto text-center text-warning">
                            <CustomIcon name='carbohydrates' />
                            {getGroupCarbohydrates(journalGroup)}g
                        </div>
                        <div className="w-20 my-auto text-center text-danger">
                            <CustomIcon name='fat' />
                            {getGroupTotalFat(journalGroup)}g
                        </div>
                    </div>
                </li>}
                {journalGroup.map((journal) => (
                    <li className="list-group-item d-flex flex-row justify-content-between px-2" key={journal.journalID}>
                        <div className={`${handleAddItem ? 'w-60 pe-1' : ''} my-auto`}>
                            <h6>{journal.aliment?.name ?? journal.recipe?.name ?? journal.meal?.name}
                                {journal.aliment && !IsNullOrEmpty(journal.aliment.brand) &&
                                    <small className="text-muted mt-0"> - {journal.aliment?.brand}</small>
                                }
                            </h6>
                            {!handleAddItem &&
                                <div className="d-flex flex-row justify-content-between mw-100">
                                    <div className="w-20 my-auto text-center">{journal.amount}{getMeasurementUnit(journal)}</div>
                                    <div className="w-20 my-auto text-center">{getCalories(journal)}kCal</div>
                                    <div className="w-20 my-auto text-center text-success">
                                        <CustomIcon name='protein' />
                                        {getProtein(journal)}g
                                    </div>
                                    <div className="w-20 my-auto text-center text-warning">
                                        <CustomIcon name='carbohydrates' />
                                        {getCarbohydrates(journal)}g
                                    </div>
                                    <div className="w-20 my-auto text-center text-danger">
                                        <CustomIcon name='fat' />
                                        {getTotalFat(journal)}g
                                    </div>
                                </div>
                            }
                        </div>
                        {handleAddItem &&
                            <div className="d-flex flex-row w-40 h-fit my-auto">
                                <FormInput
                                    entity={journal}
                                    handleInputChange={handleInputChange}
                                    fieldName='amount'
                                    mappingName={
                                        journal.alimentID
                                            ? `Aliment-${journal.alimentID}`
                                            : journal.recipeID
                                                ? `Recipe-${journal.recipeID}`
                                                : journal.mealID
                                                    ? `Meal-${journal.mealID}`
                                                    : undefined
                                    }
                                    label='Amount'
                                    type="numeric"
                                    append={measurementUnits.find(x => x.measurementUnitID == (journal.aliment?.measurementUnitID ?? 1))?.name}
                                    mb={0}
                                />
                                <i className="bi bi-trash3 ms-2 my-auto" onClick={() => handleDeleteItem(journal)} />
                            </div>
                        }
                    </li>
                ))}
            </ul>
        }
            <ul className="list-group">
                {
                    handleAddItem && !showAddItem &&
                    <li className="list-group-item text-center px-2" onClick={() => setShowAddItem(true)}>Add item</li>
                }
            </ul>
            {showAddItem &&
                <SearchContextProvider>
                    <Search handleAddItem={handleAddItem} setShowSearch={setShowAddItem} displayResultsList />
                </SearchContextProvider>
            }
        </>
    )
}